import {useAsyncRetry} from "react-use";
import {useState} from "react";
import {useApiClient} from "../../base/hooks/useApiClient";
import { Loader2 } from 'lucide-react';

const itemsPerPage = 3;

export function TransactionHistory() {
    const apiClient = useApiClient();
    const [currentPage, setCurrentPage] = useState(1);

    const { value: transactions, loading } = useAsyncRetry(async () => {
        const response = await apiClient.get('transactions');
        if (!response.ok) console.error('Error fetching transactions');
        const data = await response.json();

        return data.transactions as any[];
    }, [apiClient]);

    const totalPages = transactions ? Math.ceil(transactions.length / itemsPerPage) : 0;

    const getVisiblePages = (currentPage: number, totalPages: number) => {
        const maxVisiblePages = 5;
        const half = Math.floor(maxVisiblePages / 2);
        let start = Math.max(1, currentPage - half);
        let end = Math.min(totalPages, currentPage + half);

        if (end - start + 1 < maxVisiblePages) {
            if (start === 1) {
                end = Math.min(totalPages, start + maxVisiblePages - 1);
            } else if (end === totalPages) {
                start = Math.max(1, end - maxVisiblePages + 1);
            }
        }

        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };

    const paginatedTransactions = transactions?.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Transaction History</h2>
            {loading ? (
                <div className="flex justify-center items-center py-6">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
                </div>
            ) : (
                <>
                    {transactions?.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center">No transactions created yet.</p>
                    ) : (
                        <>
                            <div className="space-y-4">
                                {paginatedTransactions?.map(transaction => (
                                    <div key={transaction.id} className="flex items-center justify-between py-3 border-b">
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {transaction.description}
                                                {transaction.sessionName && (
                                                    <span className="text-gray-500"> ({transaction.sessionName})</span>
                                                )}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(transaction.createdAt))}
                                            </p>
                                        </div>
                                        <span className={`font-medium ${transaction.amount < -1e-10 ? 'text-red-600' : 'text-green-600'}`}>
                                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(transaction.amount)}
                                    </span>
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-center mt-4 space-x-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                                >
                                    Prev
                                </button>

                                {getVisiblePages(currentPage, totalPages).map(page => (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`px-3 py-1 rounded ${
                                            currentPage === page ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ))}

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}
